import React, { useState, useEffect } from "react";
import { X, Loader2, Download, Printer, Maximize2, ExternalLink } from "lucide-react";

/**
 * Modal para previsualización de PDF con barra de progreso interactiva
 */
const PdfPreviewModal = ({ isOpen, onClose, pdfUrl, title }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setError(false);
    }
  }, [isOpen, pdfUrl]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="px-6 py-4 bg-emerald-600 text-white flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <h3 className="font-bold text-lg leading-none">{title || "Previsualización de Reporte"}</h3>
              <p className="text-emerald-100 text-xs mt-1">Generando documento digital...</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => window.open(pdfUrl, '_blank')}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white"
              title="Abrir en pestaña nueva"
            >
              <ExternalLink className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-red-500 rounded-lg transition-all text-white ml-2"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 relative bg-gray-100 dark:bg-zinc-900">
          {/* Loading Overlay with Progress Bar */}
          {loading && (
            <div className="absolute inset-0 z-10 bg-white flex flex-col items-center justify-center p-8 text-center">
              {/* Animated Progress Bar Container */}
              <div className="w-full max-w-md bg-gray-100 rounded-full h-3 mb-6 overflow-hidden relative border border-gray-200">
                <div className="h-full bg-emerald-500 animate-progress-interact relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-3 h-3 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-3 h-3 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
                <h4 className="text-xl font-bold text-gray-800">Cargando Reporte</h4>
                <p className="text-gray-500 max-w-xs mx-auto text-sm">
                  Estamos procesando datos y todas las evidencias para generar el PDF final.
                </p>
              </div>

              {/* Styles for the interactive loading bar */}
              <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes progress-interact {
                  0% { width: 0%; }
                  20% { width: 35%; }
                  50% { width: 65%; }
                  80% { width: 85%; }
                  100% { width: 95%; }
                }
                @keyframes shimmer {
                  from { transform: translateX(-100%); }
                  to { transform: translateX(100%); }
                }
                .animate-progress-interact {
                  animation: progress-interact 12s cubic-bezier(0.1, 0.5, 0.1, 1) forwards;
                }
                .animate-shimmer {
                  animation: shimmer 1.5s infinite;
                }
              `}} />
            </div>
          )}

          {/* PDF Iframe */}
          <iframe
            src={pdfUrl}
            className={`w-full h-full border-0 transition-opacity duration-700 ${loading ? 'opacity-0' : 'opacity-100'}`}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setError(true);
            }}
            title="PDF Preview"
          />

          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white p-6 text-center">
              <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-4">
                <X className="w-10 h-10" />
              </div>
              <h4 className="text-lg font-bold text-gray-900">Error al cargar el PDF</h4>
              <p className="text-gray-500 mb-6">No pudimos conectar con el servidor para generar el reporte.</p>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all"
              >
                Cerrar y Reintentar
              </button>
            </div>
          )}
        </div>

        {/* Footer Info */}
        {!loading && !error && (
          <div className="px-6 py-3 bg-gray-50 border-t flex items-center justify-between text-[10px] text-gray-400 shrink-0">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
              <span>Documento generado satisfactoriamente</span>
            </div>
            <span>Vilox</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfPreviewModal;
